/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CDNCacheOnloadMutation } from '@/components/content/CDNCacheOnLoadMutaion';
import { CustomerService } from '@/components/content/CustomerService';
import { Notifications } from '@/components/content/Notifications';
import { NavigableSectionsProvider } from '@/components/content/ProductDetails/context/NavigableSectionsContext';
import { SelectedStoreSynchronizer } from '@/components/content/SelectedStoreSynchronizer';
import { SessionDialog } from '@/components/content/SessionDialog';
import { SessionErrorDialog } from '@/components/content/SessionErrorDialog';
import { layoutManifest } from '@/components/layouts/manifest';
import { Meta } from '@/data/Meta';
import { useStaticSettings } from '@/data/Settings';
import { SettingProvider } from '@/data/context/setting';
import { Layout } from '@/data/types/Layout';
import { ThemeSettingsProvider } from '@/styles/theme';
import { CssBaseline, SxProps } from '@mui/material';
import { Theme, ThemeProvider } from '@mui/material/styles';
import Head from 'next/head';
import { FC, useMemo } from 'react';
import { CDN_BASE_URL, HD_BASE_URL } from 'shared/custom/constants/commonConstants';

type PageProps = {
	meta: Meta;
	layout: Layout;
	theme: Theme;
	additives?: { [key: string]: SxProps | ((theme: Theme) => SxProps) };
};

declare global {
	interface Window {
		ScarabQueue: any;
	}
}
export const PageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const Layout = layout && layoutManifest[layout.name] ? layoutManifest[layout.name] : () => null;
	const themeAdditives = useMemo(() => ({ additives }), [additives]);
	const skuImageTHD = `${CDN_BASE_URL}/assets/img/logo_THD-01.svg`;
	const partNumber = layout?.tokenExternalValue;
	const page = layout?.tokenName;
	const isProductSKU = layout?.isProductSKU === 'true' ? true : false;
	const segments = meta.canonical?.split('/').filter(Boolean) ?? [''];
	const currentPath = `/${segments.slice(2).join('/')}`;
	const fullURl = `${HD_BASE_URL}${currentPath}`;

	const shouldRenderCanonical = Boolean(meta.canonical);

	return (
		<>
			<Head>
				<title>{meta?.title}</title>
				<meta name="description" content={meta?.description} />
				<meta name="keywords" content={meta?.keywords} />
				<meta property="og:title" content={meta?.title} />
				<meta property="og:description" content={meta?.description} />
				{partNumber && page === 'ProductToken' ? (
					<>
						<meta
							property="og:image"
							content={`${CDN_BASE_URL}/productos/${partNumber}/${partNumber}-d.jpg`}
						/>
						<link
							rel="preload"
							as="image"
							href={`${CDN_BASE_URL}/productos/${partNumber}/${partNumber}-d.jpg`}
							fetchPriority="high"
						/>
					</>
				) : (
					<meta property="og:image" content={skuImageTHD} />
				)}
				{isProductSKU && <meta property="og:type" content="product" />}
				<link
					rel="shortcut icon"
					href="https://cdn.homedepot.com.mx/assets/img/logo_THD-favicon.png"
				></link>
				<link
					rel="apple-touch-icon"
					sizes="180x180"
					href="https://cdn.homedepot.com.mx/assets/img/logo_THD-favicon.png"
				></link>
				<link
					rel="icon"
					type="image/png"
					sizes="16x16"
					href="https://cdn.homedepot.com.mx/assets/img/logo_THD-favicon.png"
				></link>
				{shouldRenderCanonical && <link rel="canonical" href={fullURl} />}
			</Head>
			<CustomerService />
			<CDNCacheOnloadMutation />
			<SelectedStoreSynchronizer />
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<ThemeSettingsProvider value={themeAdditives}>
					<NavigableSectionsProvider key={meta?.title}>
						<Layout layout={layout} />
					</NavigableSectionsProvider>
					<Notifications />
					<SessionErrorDialog />
					<SessionDialog />
				</ThemeSettingsProvider>
			</ThemeProvider>
		</>
	);
};

export const StaticPageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const { settings } = useStaticSettings();

	return settings ? (
		<SettingProvider value={settings}>
			<PageBlock meta={meta} layout={layout} theme={theme} additives={additives} />
		</SettingProvider>
	) : null;
};
